import { useCallback, useEffect, useState } from 'react'
import type { SetStateAction } from 'react'
import { readCookieValue, writeCookieValue } from '../helpers/cookieUtils'

type CookieChangeEvent = CustomEvent<{ key: string; value: unknown }>

function isCookieChangeEvent(event: Event): event is CookieChangeEvent {
  if (event.type !== 'cookie-change') {
    return false
  }

  // Type check for CustomEvent properties
  if (!('detail' in event)) {
    return false
  }

  // Get detail property safely - type assertion is necessary here for CustomEvent introspection
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const detail = (event as Record<string, unknown>).detail

  if (detail === null || typeof detail !== 'object' || detail === undefined) {
    return false
  }

  // Check if detail has the expected structure - safe assertion after type checks
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const detailObj = detail as Record<string, unknown>
  return 'key' in detailObj && typeof detailObj.key === 'string'
}

/**
 * A React hook that syncs state with cookies and keeps it in sync across tabs/windows.
 * The value is automatically serialized to JSON when storing and deserialized when reading.
 * Cookies are set with the .buffer.com domain for cross-subdomain access.
 *
 * @template T - The type of value being stored
 * @param {string} key - The cookie key to use
 * @param {T} defaultValue - The initial value to use if nothing is stored
 * @param {number} [maxAge] - Cookie max age in seconds (defaults to 1 year)
 * @returns {[T, (value: SetStateAction<T>) => void]} A tuple containing the current value and a setter function
 *
 * @example
 * ```tsx
 * // Simple usage with a string
 * const [name, setName] = useCookie('user-name', 'John');
 *
 * // Usage with an object
 * const [settings, setSettings] = useCookie('user-settings', {
 *   theme: 'light',
 *   notifications: true
 * });
 *
 * // Using the setter function with previous value
 * setSettings(prev => ({ ...prev, theme: 'dark' }));
 * ```
 *
 * @remarks
 * - The hook will automatically sync state across tabs/windows using the 'storage' event
 * - The value is persisted across page refreshes and subdomains
 * - The setter function accepts both direct values and updater functions
 * - If cookies are not available or errors occur, it falls back to using the default value
 * - Cookies are set with .buffer.com domain for cross-subdomain access
 */
export function useCookie<T>(
  key: string,
  defaultValue: T,
  maxAge = 365 * 24 * 60 * 60, // 1 year in seconds
): [T, (value: SetStateAction<T>) => void] {
  const [value, setValue] = useState<T>(() =>
    readCookieValue(key, defaultValue),
  )

  const handleStorageChange = useCallback(
    (event: StorageEvent | Event): void => {
      // Listen for custom cookie-change events that we dispatch
      if (isCookieChangeEvent(event)) {
        if (event.detail.key !== key) {
          return
        }
      }

      setValue(readCookieValue(key, defaultValue))
    },
    [key, defaultValue],
  )

  useEffect(() => {
    // Listen for storage events (localStorage changes) to maintain compatibility
    window.addEventListener('storage', handleStorageChange)

    // Listen for our custom cookie-change event
    window.addEventListener('cookie-change', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('cookie-change', handleStorageChange)
    }
  }, [key, defaultValue, handleStorageChange])

  const setValueInCookie = useCallback(
    (value: SetStateAction<T>): void => {
      setValue(() => {
        let updatedValue: T
        if (typeof value === 'function') {
          // Type assertion is safe here since we checked typeof === 'function'
          const updateFunction = value as (prevState: T) => T
          // refresh with latest value in case another hook or tab has updated it
          // this will most closely emulate the behavior of useState, where
          // the latest value is used in the hook even if multiple updates
          // occur in a single render cycle
          updatedValue = updateFunction(readCookieValue(key, defaultValue))
        } else {
          updatedValue = value
        }

        try {
          writeCookieValue(key, updatedValue, maxAge)

          // Dispatch a custom event to notify other useCookie hooks
          const evt = new CustomEvent('cookie-change', {
            detail: { key, value: updatedValue },
          })
          window.dispatchEvent(evt)
        } catch {
          // Silently fail - cookie storage is not critical for functionality
          // In production, this could be reported to error monitoring
        }

        return updatedValue
      })
    },
    [key, defaultValue, maxAge],
  )

  return [value, setValueInCookie]
}
